import { css, cx, etiColors, mediaQueries } from '@eti/styles';
import { useSiteContext, useTranslation } from '@eti/providers';
import { duration as durationUtil } from '@eti/utils';
import { EventSegmentType } from '@eti/schema-types';
import { getTimeBreakdown } from '../utils/format';
import { SELF_TRANSFER } from '../../../constants/eventSegmentTypes';
import SelfTransferGuarantee from '../../../common/components/SelfTransferGuarantee';
import { eventSegmentTitlesKeys } from '../../../utils/format';
import EventIcon from './TripDetailsSegmentEventIcon';

export const wrapperStyles = css`
  align-items: center;
  background-color: inherit;
  display: flex;
  flex-direction: column;
  font-size: 0.75rem;
  justify-content: center;
  margin: 2px 0 -6px;
  position: relative;

  &::before,
  &::after {
    border-top: 0.0625rem dotted ${etiColors.grayScale[10]};
    content: '';
    height: 2px;
    position: absolute;
    top: 50%;
  }

  &::before {
    margin-right: 0.5rem;
    right: 20px;
    width: 45%;
    [dir='rtl'] & {
      margin-left: 0.5rem;
      margin-right: 0;
      right: 20px;
      width: 50%;
    }

    @media ${mediaQueries.large.up} {
      right: 55px;
    }
  }

  &::after {
    left: 16px;
    margin-left: 0.5rem;
    width: 50%;
    [dir='rtl'] & {
      left: 46px;
      margin-left: 0;
      margin-right: 0.5rem;
      width: 45%;
    }

    @media ${mediaQueries.small.only} {
      left: 8px;
    }
  }
`;

const eventItemWrapperStyle = css`
  display: flex;
  justify-content: center;
  margin-bottom: 5px;
`;

const eventSegmentWrapperStyle = css`
  background-color: ${etiColors.white};
  padding: 0 10px;
  position: relative;
  z-index: 1;
`;

interface typesProps {
  legendNumber: number;
  type: string;
}
const isSelfTransfer = (types: typesProps[]) => types.some(({ type }) => type === SELF_TRANSFER);
const filterOutSelfTransfer = (types: typesProps[]) =>
  types.filter(({ type }) => type !== SELF_TRANSFER);

export interface TripDetailsEventSegmentProps {
  duration: number;
  isExpanded: boolean;
  types: typesProps[];
}
const TripDetailsEventSegment = ({
  duration,
  types = [],
  isExpanded,
}: TripDetailsEventSegmentProps) => {
  const { t } = useTranslation();
  const { language, timeFormat } = useSiteContext();

  const eventSegmentTitles = Object.fromEntries(
    Object.entries(eventSegmentTitlesKeys).map(([key, value]) => [key, t(value)]),
  );

  const mainDuration: string = durationUtil.getDuration(duration, language.code, timeFormat);

  return (
    <>
      <div
        className={cx('eventSegmentTitle', wrapperStyles)}
        data-testid="tripDetails-segment-event"
      >
        <div className={eventSegmentWrapperStyle}>
          {filterOutSelfTransfer(types).map(({ legendNumber, type }) => {
            const isOvernightStay = type === EventSegmentType.OvernightStay;

            let durationToShow = mainDuration;
            if (isOvernightStay) {
              const { hours, days, minutes } = getTimeBreakdown(mainDuration);
              const durationInHours = hours && t('TripDetails.OvernightStay.Time.Hours', hours);
              const durationInMinutes = t('TripDetails.OvernightStay.Time.Minutes', minutes);
              const shouldshowMinutes = Number(durationInMinutes) > 0 && durationInMinutes;
              durationToShow = durationInMinutes;
              // add hours text if hour is present
              if (hours) {
                durationToShow = `${durationInHours} ${shouldshowMinutes ? durationInMinutes : ''}`;
              }
              // add days text if day is present
              if (days) {
                durationToShow = `${
                  days && days > 1
                    ? t('TripDetails.OvernightStay.Time.Days', days)
                    : t('TripDetails.OvernightStay.Time.Day', days)
                } ${durationInHours}  ${shouldshowMinutes ? durationInMinutes : ''}`;
              }
            }

            return (
              <div className={eventItemWrapperStyle} key={type}>
                {legendNumber && <EventIcon legendNumber={legendNumber} />}
                <span>
                  {eventSegmentTitles[type]}: <span>{durationToShow}</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
      {isSelfTransfer(types) && <SelfTransferGuarantee isExpanded={isExpanded} />}
    </>
  );
};

export default TripDetailsEventSegment;
